import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import type { FunctionComponent, ReactNode } from 'react';
import type { Components } from 'react-markdown';
import { Link } from 'react-router';

import { Badge } from '~/ui/core/badge/Badge';
import {
    HardSkillIcon,
    IconExternalLink,
    IconLevel,
    IconRole,
    LanguageSkillIcon,
    SoftSkillIcon,
} from '~/ui/core/icons';

import type { EntityIdentifier, RichTextMentionElement } from '../../../data-contract/chat';
import { useSkillDetails } from '../../../skills';
import { ChatMarkdown } from '../../ChatMarkdown';
import { useOpenChatSkill } from '../../components/ChatSkillSidepanel';
import { MENTION_INDEX_PROPERTY, remarkMentions } from '../../markdown-mentions';
import { type NavigationTarget, toNavigationTarget } from '../text-block/mention-navigation';

const SkillMentionIcon: FunctionComponent<{ readonly skillId: string }> = ({ skillId }) => {
    const { data } = useSkillDetails(skillId);
    if (is.nullOrUndefined(data)) return null;
    if (data.skillType === 'hard') return <HardSkillIcon aria-hidden={true} data-testid="mention-icon-hard" />;
    if (data.skillType === 'language') {
        return <LanguageSkillIcon aria-hidden={true} data-testid="mention-icon-language" />;
    }
    return <SoftSkillIcon aria-hidden={true} data-testid="mention-icon-soft" />;
};

const MentionIcon: FunctionComponent<{ readonly entity: EntityIdentifier }> = ({ entity }) => {
    if (entity.type === 'role') return <IconRole size={12} aria-hidden={true} data-testid="mention-icon-role" />;
    if (entity.type === 'level') return <IconLevel size={12} aria-hidden={true} data-testid="mention-icon-level" />;
    if (entity.type === 'skill' && is.nonEmptyString(entity.id)) return <SkillMentionIcon skillId={entity.id} />;
    return null;
};

const StaticMention: FunctionComponent<{ readonly entity: EntityIdentifier; readonly label: string }> = ({
    entity,
    label,
}) => (
    <Badge variant="gray">
        <MentionIcon entity={entity} />
        {label}
    </Badge>
);

const MentionBadge: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <Badge variant="gray" onClick={noop}>
        {children}
    </Badge>
);

const LinkMention: FunctionComponent<{
    readonly entity: EntityIdentifier;
    readonly label: string;
    readonly target: NavigationTarget;
}> = ({ entity, label, target }) => {
    const content = (
        <MentionBadge>
            <MentionIcon entity={entity} />
            <span>{label}</span>
            {target.openInNewTab && <IconExternalLink aria-hidden={true} className="size-3" />}
        </MentionBadge>
    );

    if (target.openInNewTab) {
        return (
            <a className="no-underline" href={target.href} rel="noreferrer" target="_blank">
                {content}
            </a>
        );
    }

    return (
        <Link className="no-underline" to={target.href}>
            {content}
        </Link>
    );
};

const SkillMention: FunctionComponent<{
    readonly entity: EntityIdentifier;
    readonly label: string;
    readonly skillId: string;
}> = ({ entity, label, skillId }) => {
    const openSkill = useOpenChatSkill();
    return (
        <Badge variant="gray" onClick={() => openSkill(skillId)}>
            <MentionIcon entity={entity} />
            {label}
        </Badge>
    );
};

const MentionView: FunctionComponent<{ readonly element: RichTextMentionElement }> = ({ element }) => {
    // Skill mentions open the in-chat skill details panel (like the diff rows) instead of
    // deep-linking to the board.
    if (element.entity.type === 'skill' && is.nonEmptyString(element.entity.id)) {
        return <SkillMention entity={element.entity} label={element.label} skillId={element.entity.id} />;
    }
    const target = toNavigationTarget(element.entity, element.openInNewTab ?? false);
    if (is.null(target)) return <StaticMention entity={element.entity} label={element.label} />;
    return <LinkMention entity={element.entity} label={element.label} target={target} />;
};

export const createMentionComponents = (mentions: readonly RichTextMentionElement[]): Components => ({
    span: ({ node, children, ...props }) => {
        const index = node?.properties?.[MENTION_INDEX_PROPERTY];
        const mention = is.number(index) ? mentions[index] : undefined;
        if (is.undefined(mention)) return <span {...props}>{children}</span>;
        return <MentionView element={mention} />;
    },
});

// Renders markdown with mentions interleaved at their sentinel positions. Pair with
// `serializeRichTextToMarkdown` (rich-text content) or `parseMentionMarkup` (inline ⟦…⟧ markup).
export const MentionMarkdown: FunctionComponent<{
    readonly markdown: string;
    readonly mentions: readonly RichTextMentionElement[];
}> = ({ markdown, mentions }) => (
    <ChatMarkdown components={createMentionComponents(mentions)} remarkPlugins={[remarkMentions]}>
        {markdown}
    </ChatMarkdown>
);
